import { useEffect, useState } from "react";
import { doc, onSnapshot, Timestamp } from "firebase/firestore";
import { db } from "@/firebase";
import { useAuthState } from "@/auth/AuthProvider";

declare global {
  interface Window {
    __USER_SUB_ACTIVE__?: boolean;
  }
}

type SubscriptionStatus = "active" | "inactive" | "canceled" | "trialing" | "past_due" | "none" | string;

type SubscriptionState = {
  active: boolean;
  status: SubscriptionStatus;
  plan?: string;
  currentPeriodEnd?: Date;
  loading: boolean;
};

export function useSubscription(): SubscriptionState {
  const { user } = useAuthState();
  const [state, setState] = useState<SubscriptionState>({
    active: !!window.__USER_SUB_ACTIVE__,
    status: window.__USER_SUB_ACTIVE__ ? "active" : "none",
    plan: undefined,
    currentPeriodEnd: undefined,
    loading: true,
  });

  useEffect(() => {
    if (!user?.uid) {
      const active = !!window.__USER_SUB_ACTIVE__;
      setState({ active, status: active ? "active" : "none", plan: undefined, currentPeriodEnd: undefined, loading: false });
      return;
    }

    setState((s) => ({ ...s, loading: true }));
    const ref = doc(db, "users", user.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = (snap.data() || {}) as {
          subscription?: { status?: SubscriptionStatus; plan?: string; currentPeriodEnd?: unknown };
          billing?: { subActive?: boolean };
        };

        const status = (snap.get("subscription.status") as SubscriptionStatus | undefined)
          ?? data.subscription?.status
          ?? (data.billing?.subActive ? "active" : "none");
        const plan = (snap.get("subscription.plan") as string | undefined) ?? data.subscription?.plan;
        const rawEnd = snap.get("subscription.currentPeriodEnd") ?? data.subscription?.currentPeriodEnd;

        let currentPeriodEnd: Date | undefined;
        if (rawEnd instanceof Date) currentPeriodEnd = rawEnd;
        else if (rawEnd && typeof (rawEnd as Timestamp).toDate === "function") currentPeriodEnd = (rawEnd as Timestamp).toDate();
        else if (typeof rawEnd === "number") currentPeriodEnd = new Date(rawEnd * 1000);
        else currentPeriodEnd = undefined;

        const active = status === "active" || !!data.billing?.subActive;
        setState({ active, status, plan, currentPeriodEnd, loading: false });
      },
      () => {
        const active = !!window.__USER_SUB_ACTIVE__;
        setState({ active, status: active ? "active" : "none", plan: undefined, currentPeriodEnd: undefined, loading: false });
      }
    );

    return () => unsub();
  }, [user?.uid]);

  return state;
}
